/**
 * Hydrate deferred lists (AB#536).
 *
 * WHAT AND WHY, measured rather than assumed.
 *
 * The product page's LCP was 2236ms against the LIVE baseline's 1852ms, and the phase breakdown
 * said the gap was not where anyone would guess:
 *
 *     phase                 V2        baseline
 *     TTFB                  192ms     218ms      V2 faster
 *     resource load delay   459ms     424ms      the same
 *     resource load time     55ms     967ms      V2 much faster
 *     RENDER DELAY         1530ms     243ms      V2 1287ms worse
 *
 * The LCP element is the main product image, it is eager and `fetchpriority="high"`, and it
 * ARRIVES IN 55ms. The page then takes another 1.5 seconds to paint it, because the main thread
 * is busy. So the fix is not anything to do with the image.
 *
 * A DOM census found 760 `.localization-form__list-item` elements on a V2 product page and ZERO
 * on the baseline, which renders its country selector differently. O5 requires the country
 * selector in the footer AND the menu sheet, so the page carries several localization forms of
 * roughly 250 countries each. All of it is inside closed dialogs nobody has opened.
 *
 * Inside a `<template>` those nodes are inert: the parser reads them, but they are not in the
 * document tree, so style resolution and layout skip them. This file puts them back.
 *
 * WHEN IT HYDRATES, and why both triggers exist:
 *
 *   1. On first interaction with the selector's container. This is the one that matters for
 *      correctness: whatever else happens, the list is there before anyone can use it.
 *   2. Otherwise during idle time after load. Without this, the list would only ever exist after
 *      a click, and Horizon's country search JS queries for the items when it initialises. Idle
 *      hydration keeps that working without putting the work back on the critical path.
 *
 * It is deliberately NOT tied to the dialog's `open` state. A dialog can be opened by markup, by
 * script, by the back button restoring state, or by a test; "first interaction OR idle" covers
 * all of those without needing to know which one happened.
 */

const HYDRATED = 'data-mlr-hydrated';

/** Move one template's content into the document, once. */
function hydrate(template) {
  if (!template || template.hasAttribute(HYDRATED)) return;
  template.setAttribute(HYDRATED, '');
  const parent = template.parentNode;
  if (!parent) return;
  // Insert BEFORE the template so the list keeps its position among any siblings, then drop the
  // now-empty template. Replacing it outright would be equivalent here, but this survives a
  // snippet that later adds markup after the list.
  parent.insertBefore(template.content.cloneNode(true), template);
  template.remove();
}

function hydrateAll(root = document) {
  for (const t of root.querySelectorAll('template[data-mlr-deferred-list]')) hydrate(t);
}

/**
 * Hydrate the templates inside whatever container the interaction happened in, rather than the
 * whole page: opening the footer selector should not pay for the menu sheet's list as well.
 */
function hydrateNear(target) {
  if (!(target instanceof Element)) return hydrateAll();
  const scope =
    target.closest('.localization-form, country-selector-component, dialog, details, .mlr-menu, footer') ??
    document;
  hydrateAll(scope);
}

// 1. First interaction wins, in the capture phase, so the list exists before any other handler
//    on the same event goes looking for it.
for (const type of ['pointerdown', 'focusin', 'keydown']) {
  document.addEventListener(type, (event) => hydrateNear(event.target), { capture: true });
}

// 2. Idle fallback. `requestIdleCallback` is not in Safari before 17, so a timeout stands in: the
//    exact moment does not matter, only that it is after the page has painted.
const idle = (fn) =>
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(fn, { timeout: 4000 })
    : setTimeout(fn, 1500);

if (document.readyState === 'complete') {
  idle(() => hydrateAll());
} else {
  window.addEventListener('load', () => idle(() => hydrateAll()), { once: true });
}

// Exported for tests, which need to force the state rather than wait for an idle callback.
window.mlrHydrateDeferredLists = hydrateAll;
